// Persistent Video.js player across the rotate remount.
//
// Rotating a phone portrait <-> immersive landscape makes Experience.render()
// return a different subtree, so React unmounts and remounts <Video>. Creating a
// fresh video.js player on every rotate is the shared root cause of two bugs:
//
//   - mutes after a few rotates: the fresh <video> must play() to
//     resume, an unmuted programmatic play() on a fresh element is rejected by
//     iOS's autoplay policy (a rotation is not a user gesture), so the resume
//     path force-mutes to keep playing — and the next unmount saves that muted
//     flag, ratcheting the video permanently muted.
//   - poster covers the video + side gap after landscape->portrait: the
//     fresh player shows its poster and mis-sizes before the source reloads.
//
// This module keeps ONE live player alive across the remount, keyed by
// experience id. On unmount the live `.video-js` node is PARKED in an off-screen
// holder (kept IN the document, so iOS doesn't pause the media the instant it
// leaves a layout). On the next mount for the SAME experience the node is
// ADOPTED back into the freshly-rendered host inside the active layout container.
// Because it is the same element — which already earned playback permission — no
// resume play() is needed: sound, position and mute state all survive, and the
// poster never re-shows.
//
// A chain advance loads a DIFFERENT experience: it flips the store to LOADING,
// which unmounts Experience (not just <Video>), so Experience.componentWillUnmount
// disposes the entry and the incoming experience gets a fresh player. `acquire`
// also refuses to hand back a player registered under a different id, so the
// create path never adopts a stale player.

import type Player from "video.js/dist/types/player";

let _entry: { idexperience: unknown; player: Player } | null = null;
let _holder: HTMLDivElement | null = null;

function _ensureHolder(): HTMLDivElement {
  if (_holder && _holder.parentNode) {
    return _holder;
  }
  _holder = document.createElement("div");
  _holder.className = "pix-player-holder";
  _holder.setAttribute("aria-hidden", "true");
  if (document.body) {
    document.body.appendChild(_holder);
  }
  return _holder;
}

// The live player for `idexperience` if one is parked/registered, else null. A
// different id (chain advance to a new experience) returns null so the caller
// creates a fresh player rather than adopting a stale one.
export function acquire(idexperience: unknown): Player | null {
  if (
    idexperience != null &&
    _entry &&
    _entry.idexperience === idexperience &&
    _entry.player &&
    !_entry.player.isDisposed()
  ) {
    return _entry.player;
  }
  return null;
}

export function register(idexperience: unknown, player: Player) {
  _entry = { idexperience: idexperience, player: player };
}

// The live player for whichever experience is currently mounted, or null. Lets
// sibling UI (e.g. the MCP control row's volume + quality controls) reach the
// video.js player without threading a ref through React — there is only ever one
// live entry, and it is the active experience's player.
export function current(): Player | null {
  return _entry && _entry.player && !_entry.player.isDisposed()
    ? _entry.player
    : null;
}

// Move the live `.video-js` node out of the doomed layout subtree into the
// off-screen holder BEFORE React tears the old layout down, so the media element
// is never detached from the document (detaching pauses playback on iOS).
export function park(player: Player | null | undefined) {
  if (!player || player.isDisposed()) {
    return;
  }
  const node = player.el();
  const holder = _ensureHolder();
  if (node && holder && node.parentNode !== holder) {
    holder.appendChild(node);
  }
}

// Move the live `.video-js` node into the freshly-rendered host inside the active
// layout container. appendChild RELOCATES the node (with the live player inside)
// — it is not cloned or recreated — so playback and mute state survive the move.
export function adopt(
  player: Player | null | undefined,
  host: Element | null | undefined,
) {
  if (!player || player.isDisposed() || !host) {
    return;
  }
  const node = player.el();
  if (node && node.parentNode !== host) {
    host.appendChild(node);
  }
}

// Dispose the entry for `idexperience` (or the sole entry when id is omitted).
// Called from Experience.componentWillUnmount on a true teardown / chain advance.
export function dispose(idexperience?: unknown) {
  if (!_entry) {
    return;
  }
  if (idexperience != null && _entry.idexperience !== idexperience) {
    return;
  }
  try {
    if (_entry.player && !_entry.player.isDisposed()) {
      _entry.player.dispose();
    }
  } catch {
    /* ignore — disposing an already-torn-down player */
  }
  _entry = null;
}

// Belt-and-suspenders for the create path: if a stale entry for a DIFFERENT
// experience somehow survived (Experience.componentWillUnmount should have
// disposed it), tear it down before creating the new player so it can't leak.
export function disposeStale(idexperience: unknown) {
  if (_entry && _entry.idexperience !== idexperience) {
    dispose();
  }
}
